import { Component, OnInit, OnDestroy } from '@angular/core';

import { MediaTypeColorPickerWrapperFieldComponent } from '../MediaTypeColorPickerWrapperField/media-type-color-picker-wrapper-field.component';
import { MonitorConfigService } from '../../../../MonitorShared/services/monitor-config.service';
import { MediaTypeEnum, getSocialMediaTypes, getSocialMediaPlatformTypes, mediaTypeColorKeyMap } from '@Common/Basic/enums/media-type.enum';
import { MediaTypeCollectionTypesEnum } from '../../../models/media-type-color-picker-wrapper-field.model';
import { ChartDisplayElementModel } from '../../../../Reports/models/chart-display-element.model';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { ChartColorPickerFieldModel } from '@Common/PageBuilder/models/chart-color-picker-field.model';

@Component({
    selector: 'agpr-optional-media-type-color-picker-field',
    templateUrl: './optional-media-type-color-picker-field.component.html',
    styleUrls: ['./optional-media-type-color-picker-field.component.scss'],
    standalone: false
})
export class OptionalMediaTypeColorPickerFieldComponent extends MediaTypeColorPickerWrapperFieldComponent implements OnInit, OnDestroy {

    private readonly MEDIA_TYPE_COLORS = [
        MediaTypeEnum.ONLINE_NEWS,
        MediaTypeEnum.PRINT,
        MediaTypeEnum.PRESS_RELEASE,
        MediaTypeEnum.BLOGS,
        MediaTypeEnum.TV,
        MediaTypeEnum.RADIO,
        MediaTypeEnum.PODCAST
    ];

    private readonly CORE_SOCIAL_MEDIA = [
        MediaTypeEnum.FACEBOOK,
        MediaTypeEnum.TWITTER,
        MediaTypeEnum.INSTAGRAM,
        MediaTypeEnum.YOUTUBE,
        MediaTypeEnum.REDDIT,
        MediaTypeEnum.TIKTOK,
        MediaTypeEnum.LINKEDIN,
        MediaTypeEnum.BLUESKY
    ];

    private readonly OTHER_SOCIAL_MEDIA = [
        MediaTypeEnum.MAGAZINE,
        MediaTypeEnum.PINTEREST,
        MediaTypeEnum.SINA_WEIBO,
        MediaTypeEnum.SNAPCHAT,
        MediaTypeEnum.TELEGRAM,
        MediaTypeEnum.THREADS,
        MediaTypeEnum.TUMBLR,
        MediaTypeEnum.TWITCH,
        MediaTypeEnum.WECHAT,
        MediaTypeEnum.WHATSAPP,
        MediaTypeEnum.VK,
        MediaTypeEnum.OTHER
    ];

    readonly buttonTypes = ButtonTypesEnum;

    allSelected: boolean;

    colorSettings: [string, MediaTypeEnum][];
    valueMap: { [key: string]: boolean } = {};

    mediaTypeColors: ChartColorPickerFieldModel[] = [];
    coreSocialColors: ChartColorPickerFieldModel[] = [];
    otherSocialColors: ChartColorPickerFieldModel[] = [];

    constructor(
        public monitorConfigService: MonitorConfigService
    ) {
        super(monitorConfigService);
    }

    ngOnInit() {
        this.subscriptions.push(
            this.monitorConfigService.getAvailableMediaTypes().subscribe(mediaTypes => {
                const allMediaTypes = Object.entries(MediaTypeEnum);

                switch (this.field.mediaTypeCollectionType) {
                    case MediaTypeCollectionTypesEnum.CORE_SOCIAL_MEDIA_TYPE_ONLY:
                        const socialMediaTypes: MediaTypeEnum[] = getSocialMediaTypes();
                        this.colorSettings = allMediaTypes
                            .filter(([k, v]) => socialMediaTypes.includes(v))
                            .map(([k, v]) => [mediaTypeColorKeyMap.get(v) ?? k, v]);
                        break;
                    case MediaTypeCollectionTypesEnum.SOCIAL_MEDIA_TYPES_ONLY:
                        const socialMediaPlatformTypes = getSocialMediaPlatformTypes();
                        this.colorSettings = allMediaTypes
                            .filter(([k, v]) => socialMediaPlatformTypes.includes(v))
                            .map(([k, v]) => [mediaTypeColorKeyMap.get(v) ?? k, v]);
                        break;
                    case MediaTypeCollectionTypesEnum.ALL:
                        this.colorSettings = allMediaTypes.map(([k, v]) => [mediaTypeColorKeyMap.get(v) ?? k, v]);
                        break;
                }

                if (!mediaTypes.includes(MediaTypeEnum.TIKTOK)) {
                    this.colorSettings = this.colorSettings.filter(([k, v]) => v !== MediaTypeEnum.TIKTOK);
                }

                if (!(this.block.displayElement as ChartDisplayElementModel).networkSelection) {
                    this.colorSettings.forEach(([k, v]) => this.valueMap[v] = true);
                    (this.block.displayElement as ChartDisplayElementModel).networkSelection = this.colorSettings
                        .map((k, v) => k[1]);
                    this.allSelected = true;
                } else {
                    this.colorSettings.forEach(([k, v]) => this.valueMap[v] =
                        (this.block.displayElement as ChartDisplayElementModel).networkSelection.includes(v));
                    this.allSelected = this.colorSettings.every(([k, v]) => this.valueMap[v]);
                }

                this.colorFieldModels = super.mapObjectEntriesToFieldModels(this.colorSettings);

                this.mediaTypeColors = this.MEDIA_TYPE_COLORS
                    .map(mediaType => this.colorFieldModels.find(
                        field => field.key === mediaType
                    ))
                    .filter(field => field !== undefined);

                this.coreSocialColors = this.CORE_SOCIAL_MEDIA
                    .map(mediaType => this.colorFieldModels.find(
                        field => field.key === mediaType
                    ))
                    .filter(field => field !== undefined);

                this.otherSocialColors = this.OTHER_SOCIAL_MEDIA
                    .map(mediaType => this.colorFieldModels.find(
                        field => field.key === mediaType
                    ))
                    .filter(field => field !== undefined);
            })
        );
    }

    handleChanges() {
        (this.block.displayElement as ChartDisplayElementModel).networkSelection = this.colorSettings.filter(([k, v]) => this.valueMap[v]).map(([k, v]) => v);
        this.allSelected = this.colorSettings.every(([k, v]) => this.valueMap[v]);
    }

    toggleSelectAll() {
        if (this.allSelected) {
            this.colorSettings.forEach(([k, v]) => this.valueMap[v] = false);
        } else {
            this.colorSettings.forEach(([k, v]) => this.valueMap[v] = true);
        }
        this.handleChanges();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
